import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { useState } from "react";
import { RelatedServices } from "@/components/RelatedServices";
import { Globe, Server, Mail, Search, Shield, RefreshCw, ArrowRight, Check } from "lucide-react";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function DomainRegistration() {
  const { openDrawer } = useQuoteDrawer();
  const [searchTerm, setSearchTerm] = useState("");

  const features = [
    {
      title: "Domain Search & Registration",
      description: "Find and register your perfect domain name with instant availability checking.",
      icon: <Search className="h-8 w-8" />
    },
    {
      title: "Domain Management",
      description: "Easy-to-use control panel for managing DNS, redirects, and domain settings.",
      icon: <Globe className="h-8 w-8" />
    },
    {
      title: "Domain Protection",
      description: "Privacy protection, domain lock, and security features to keep your domain safe.",
      icon: <Shield className="h-8 w-8" />
    },
    {
      title: "Domain Transfer",
      description: "Seamless domain transfer services with expert support throughout the process.",
      icon: <RefreshCw className="h-8 w-8" />
    }
  ];

  const domainExtensions = [
    { extension: ".com", price: "$12.99", description: "Most popular for businesses" },
    { extension: ".ae", price: "$49.99", description: "UAE country domain" },
    { extension: ".org", price: "$14.99", description: "For organizations" },
    { extension: ".net", price: "$15.99", description: "Network & tech companies" },
    { extension: ".info", price: "$18.99", description: "Information websites" },
    { extension: ".biz", price: "$19.99", description: "Business domains" }
  ];

  const handleDomainSearch = (e: React.MouseEvent) => {
    e.preventDefault();
    // This would integrate with domain search API
    console.log("Searching for domain:", searchTerm);
    // Open the quote drawer after search
    openDrawer();
  };

  return (
    <>
      <SEO
        title="Domain Registration Services UAE | First Soft UAE"
        description="Register your domain name in UAE. .com, .ae, .org domains available. Domain search, registration, transfer, and management services."
        keywords="domain registration UAE, buy domain Dubai, .ae domain Sharjah, domain transfer UAE"
      />

      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Floating domain names */}
          {[...Array(10)].map((_, i) => (
            <div 
              key={i}
              className="absolute text-gray-300/20 font-mono text-lg md:text-xl"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${Math.random() * 10 + 5}s infinite alternate`
              }}
            >
              {['.com', '.net', '.io', '.org'][i % 4]}
            </div>
          ))}
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                  Domain Registration
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-lg">
                Secure your perfect domain name with instant activation and free privacy protection.
              </p>
              
              {/* Domain search */}
              <div className="relative max-w-xl mb-12">
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleDomainSearch(e as any)}
                  placeholder="Find your perfect domain..." 
                  className="w-full px-6 py-4 bg-white/80 backdrop-blur-sm border border-gray-300 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button 
                  onClick={handleDomainSearch}
                  className="absolute right-2 top-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-md font-medium text-white transition-colors"
                >
                  Search
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                {['.com', '.net', '.io', '.dev', '.app'].map((tld) => (
                  <span key={tld} className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-gray-700 border border-gray-300 text-sm">
                    {tld}
                  </span>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Glass morphism card */}
              <div className="relative bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl p-8 shadow-lg overflow-hidden">
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-blue-500/5 rounded-full filter blur-xl"></div>
                
                {/* Domain status example */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Domain Availability</h3>
                    <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                  </div>
                  
                  <div className="space-y-3">
                    {[
                      { domain: 'yourbusiness.com', status: 'Available', price: '$12.99' },
                      { domain: 'yourbusiness.net', status: 'Available', price: '$14.99' },
                      { domain: 'yourbusiness.io', status: 'Taken', price: '' }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="font-mono text-gray-700">{item.domain}</div>
                        <div className={`text-sm font-medium ${item.status === 'Available' ? 'text-green-600' : 'text-gray-500'}`}>
                          {item.status === 'Available' ? (
                            <span className="flex items-center">
                              {item.price} <ArrowRight className="ml-2 h-4 w-4" />
                            </span>
                          ) : 'Unavailable'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Domain features */}
                <div className="grid grid-cols-2 gap-4">
                  {['Free Privacy', 'DNS Management', 'Email Forwarding', '24/7 Support'].map((feature) => (
                    <div key={feature} className="p-2 bg-gray-50 rounded-lg border border-gray-200 flex items-center">
                      <div className="h-6 w-6 bg-blue-100 rounded-lg mr-2 flex items-center justify-center">
                        <Check className="h-3 w-3 text-blue-600" />
                      </div>
                      <div className="text-sm text-gray-700">{feature}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Floating badges */}
              <div className="absolute -bottom-5 -right-5 flex space-x-2">
                {['IMAP', 'POP3', 'SMTP', 'Exchange'].map((protocol) => (
                  <span key={protocol} className="px-2 py-1 text-xs font-medium rounded-full bg-white/80 text-gray-700 backdrop-blur-sm">
                    {protocol}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Domain Services</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Complete domain solutions for your online presence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-8 rounded-xl"
              >
                <div className="w-16 h-16 bg-secondary/20 rounded-lg flex items-center justify-center mb-6 text-secondary">
                  {feature.icon}
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-4">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Domain Extensions & Pricing */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Domain Extensions & Pricing</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Choose from a wide range of domain extensions at competitive prices
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domainExtensions.map((domain, index) => (
              <motion.div
                key={domain.extension}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-6 rounded-xl"
              >
                <div className="text-center">
                  <h3 className="font-orbitron font-bold text-2xl text-secondary mb-2">{domain.extension}</h3>
                  <p className="text-3xl font-bold text-primary mb-2">{domain.price}</p>
                  <p className="text-sm text-muted-foreground mb-4">per year</p>
                  <p className="text-muted-foreground mb-4">{domain.description}</p>
                  <Button onClick={openDrawer} className="btn-secondary w-full">
                    Register Now
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <RelatedServices 
            currentService="Domain Registration" 
            services={[
              {
                title: "Web Hosting",
                description: "Reliable hosting solutions to power your website after domain registration.",
                icon: <Server className="h-8 w-8" />
              },
              {
                title: "Email Hosting",
                description: "Professional email services with your custom domain name.",
                icon: <Mail className="h-8 w-8" />
              },
              {
                title: "Web Design & Development",
                description: "Build a professional website for your newly registered domain.",
                icon: <Globe className="h-8 w-8" />
              }
            ]} 
          />
        </div>
      </section>

      {/* Domain Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Why Choose Our Domain Services?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Professional domain management with added benefits
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Free DNS Management",
                description: "Complete DNS control with easy-to-use management interface",
                icon: "fas fa-cogs"
              },
              {
                title: "Domain Privacy Protection",
                description: "Protect your personal information from public WHOIS databases",
                icon: "fas fa-user-shield"
              },
              {
                title: "24/7 Expert Support",
                description: "Round-the-clock assistance from domain specialists",
                icon: "fas fa-headset"
              }
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-secondary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-xl text-primary-foreground`}></i>
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-4">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Web Design Locations */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <WebDesignLocations />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-secondary/10 to-accent/10">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">
              Ready to Claim Your Domain?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Secure your perfect domain name today and establish your online presence
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={openDrawer} className="btn-primary">
                Register Domain Now
              </Button>
              <Link href="/contact">
                <Button className="btn-secondary">
                  Need Help Choosing?
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
